import {
  PayrollRun,
  PayrollRunFailureCode
} from "@wingspanhq/wingspan-api-sdk";

export type PayrollRunFailureAlert = {
  title: string;
  description: string;
  /** Whether the funding-settings action can do anything about this cause. */
  isFundingActionable: boolean;
};

const BY_CODE: Record<PayrollRunFailureCode, PayrollRunFailureAlert> = {
  [PayrollRunFailureCode.FUNDING_SOURCE_MISSING]: {
    title: "No payroll funding account",
    description:
      "This run could not be paid because no payroll funding account is set up. Add one, then contact us to reprocess the payment.",
    isFundingActionable: true
  },
  [PayrollRunFailureCode.FUNDING_ACCOUNT_NOT_READY]: {
    title: "Payroll funding account isn't ready",
    description:
      "This run could not be paid because the payroll funding account is not ready to be debited yet. Finish verifying it, then contact us to reprocess the payment.",
    isFundingActionable: true
  },
  [PayrollRunFailureCode.INSUFFICIENT_FUNDS]: {
    title: "Not enough funds to cover this payroll",
    description:
      "The payroll funding account did not have enough available balance to cover this run. Add funds or choose another account, then contact us to reprocess the payment.",
    isFundingActionable: true
  },
  [PayrollRunFailureCode.FUNDING_SUBMISSION_FAILED]: {
    title: "This payroll run could not be submitted",
    description:
      "The payables in this run were not paid out because the debit could not be submitted to your bank. Contact us to reprocess the payment.",
    isFundingActionable: false
  },
  [PayrollRunFailureCode.FUNDING_DEBIT_RETURNED]: {
    title: "Your bank returned the payroll debit",
    description:
      "The payables in this run were not paid out because your bank returned the debit. Check that the payroll funding account has sufficient funds, or update the account you use for payroll funding, then contact us to reprocess the payment.",
    isFundingActionable: true
  }
};

/**
 * What to tell a payer about a failed contractor run.
 *
 * Until SDK 4.x a `PayrollRun` carried no failure reason (gap G1), so the screen
 * named the most likely cause — a returned debit — on every failure. `Failed`
 * now carries `failureReason.code`, so each cause gets its own copy and the
 * funding-settings action is offered only where it is the fix.
 *
 * Falls back to the old wording when the run is failed but names no code, which
 * is what the runs that failed before the field existed look like.
 */
export const selectorPayrollRunFailureAlert = (
  payrollRun?: PayrollRun
): PayrollRunFailureAlert => {
  const code = payrollRun?.failureReason?.code;

  return (
    (code && BY_CODE[code]) || {
      title: "This payroll run was unsuccessful",
      description:
        "The payables in this run were not paid out. Check that the payroll funding account has sufficient funds, or update the account you use for payroll funding. Contact us to reprocess the payment once that's done.",
      isFundingActionable: true
    }
  );
};
